import { PartialConnection } from './partialConnection';
import * as dayjs from 'dayjs';
import { TRANSITION_TYPE, TRANSPORTATION_CATEGORY } from '../../enums/model.enums';
import { TariffInformation } from '../tariffInformation/tariffInformation';
import { MetaData } from '../MetaData';
import { isNullOrUndefined, isUndefined } from 'util';
import { Ticket } from '../tickets/ticket';
/**
 * class {Connection}
 * should be used to display connection related information
 */
export class Connection extends MetaData {
    /**
     * constructor of class
     * please use factory instead
     * @see {this.factory)
     * @param id
     * @param price
     * @param partialConnections
     * @param tariffInformation
     * @param sessionId
     * @param requestId
     * @param distance
     * @param availableTickets
     * @param metaData
     */
    constructor(id, price, partialConnections, tariffInformation, sessionId, requestId, distance, availableTickets, metaData) {
        super(metaData);
        this._id = id;
        this._price = price;
        this._partialConnections = partialConnections;
        this._tariffInformation = tariffInformation;
        this._sessionId = sessionId;
        this._requestId = requestId;
        this._distance = distance;
        this._availableTickets = availableTickets;
    }
    /**
     * Factory method to create an instance of this class without
     * having to correctly fill the constructor.
     * !!! You should always use this instead of the constructor !!!
     * @param values Object of values to inflate the object with
     * @returns {Connection}
     */
    static factory(values) {
        const sanitizedValues = {};
        sanitizedValues.id = values.id || '';
        sanitizedValues.price = values.price || 0;
        sanitizedValues.partialConnections = [];
        if (values.partialConnections) {
            for (const partial of values.partialConnections) {
                sanitizedValues.partialConnections.push(PartialConnection.factory(partial));
            }
        }
        sanitizedValues.tariffInformation = values.tariffInformation
            ? TariffInformation.factory(values.tariffInformation)
            : TariffInformation.factory({});
        sanitizedValues.sessionId = values.sessionId || '';
        sanitizedValues.requestId = values.requestId || '';
        sanitizedValues.distance = !isUndefined(values.distance) ? values.distance : 0;
        sanitizedValues.availableTickets = [];
        if (values.availableTickets) {
            for (const ticket of values.availableTickets) {
                sanitizedValues.availableTickets.push(Ticket.factory(ticket));
            }
        }
        sanitizedValues.metaData = !isUndefined(values.metaData) ? values.metaData : {};
        return new Connection(sanitizedValues.id, sanitizedValues.price, sanitizedValues.partialConnections, sanitizedValues.tariffInformation, sanitizedValues.sessionId, sanitizedValues.requestId, sanitizedValues.distance, sanitizedValues.availableTickets, sanitizedValues.metaData);
    }
    /**
     * gets the internal _id
     * @returns {string}
     */
    get id() {
        return this._id;
    }
    /**
     * sets the internal _id
     * @param value
     */
    set id(value) {
        this._id = value;
    }
    /**
     * gets price for current connection
     * @returns {number}
     */
    get price() {
        return this._price;
    }
    /**
     * sets price of current connection
     * @param value
     */
    set price(value) {
        this._price = value;
    }
    /**
     * gets the partial connections for the current connection
     * @returns {PartialConnection[]}
     */
    get partialConnections() {
        return this._partialConnections;
    }
    /**
     * sets the partial connections for the current connection
     * @param value
     */
    set partialConnections(value) {
        this._partialConnections = value;
    }
    /**
     * gets the TariffInformation for the current connection
     * @returns {TariffInformation}
     */
    get tariffInformation() {
        return this._tariffInformation;
    }
    /**
     * sets the TariffInformation for the current connection
     * @param value
     */
    set tariffInformation(value) {
        this._tariffInformation = value;
    }
    /**
     * gets the sessionId for the current connection
     * @returns {string}
     */
    get sessionId() {
        return this._sessionId;
    }
    /**
     * sets the sessionId for the current connection
     * @param value
     */
    set sessionId(value) {
        this._sessionId = value;
    }
    /**
     * gets the requestId for the current connection
     * @returns {string}
     */
    get requestId() {
        return this._requestId;
    }
    /**
     * sets the requestId for the current connection
     * @param value
     */
    set requestId(value) {
        this._requestId = value;
    }
    /**
     * gets the distance for the current connection
     * @returns {number}
     */
    get distance() {
        return this._distance;
    }
    /**
     * sets the distance for the current connection
     */
    set distance(value) {
        this._distance = value;
    }
    /**
     * gets the available tickets for the current connection
     * @returns {Ticket[]}
     */
    get availableTickets() {
        return this._availableTickets;
    }
    /**
     * sets the available tickets for the current connection
     * @param value
     */
    set availableTickets(value) {
        this._availableTickets = value;
    }
    /**
     * get the origin of the current connection based on existing partial connections
     * (gets origin of first partial connection)
     * @returns {TimetableTrafficNode} or {undefined} if no partial connection is in {_partialConnections}
     */
    get origin() {
        return this._partialConnections[0] ? this._partialConnections[0].origin : undefined;
    }
    /**
     * get the destination of the current connection based on existing partial connections
     * (gets destination of last partial connection)
     * @returns {TimetableTrafficNode} or {undefined} if no partial connection is in {_partialConnections}
     */
    get destination() {
        return this._partialConnections.length > 0 && this._partialConnections[this._partialConnections.length - 1]
            ? this._partialConnections[this._partialConnections.length - 1].destination
            : undefined;
    }
    /**
     * get the start date-time of the current connection based on existing partial connections
     * (gets start date-time of first partial connection)
     * @returns {Date} or {undefined} if no partial connection is in {_partialConnections}
     */
    get startDateTime() {
        return this._partialConnections[0] ? this._partialConnections[0].startDateTime : undefined;
    }
    /**
     * get the end date-time of the current connection based on existing partial connections
     * (gets end date-time of last partial connection)
     * @returns {Date} or {undefined} if no partial connection is in {_partialConnections}
     */
    get endDateTime() {
        return this._partialConnections.length > 0 && this._partialConnections[this._partialConnections.length - 1]
            ? this._partialConnections[this._partialConnections.length - 1].endDateTime
            : undefined;
    }
    /**
     * get the actual start date-time of the current connection based on existing partial connections
     * (gets start date-time of first partial connection)
     * @returns {Date} or {undefined} if no partial connection is in {_partialConnections}
     */
    get actualStartDateTime() {
        return this._partialConnections[0] ? this._partialConnections[0].actualStartDateTime : undefined;
    }
    /**
     * get the actual end date-time of the current connection based on existing partial connections
     * (gets end date-time of last partial connection)
     * @returns {Date} or {undefined} if no partial connection is in {_partialConnections}
     */
    get actualEndDateTime() {
        return this._partialConnections.length > 0 && this._partialConnections[this._partialConnections.length - 1]
            ? this._partialConnections[this._partialConnections.length - 1].actualEndDateTime
            : undefined;
    }
    /**
     * gets the duration of the current connection based on existing partial connections
     * (gets the calculated duration from first partial connection start date-time to last partial connection end date-time)
     * @returns {number}
     */
    get duration() {
        if (!isNullOrUndefined(this.actualStartDateTime) && !isNullOrUndefined(this.actualEndDateTime)) {
            return dayjs(this.actualEndDateTime).diff(dayjs(this.actualStartDateTime), 'minute');
        }
        else if (!isNullOrUndefined(this.actualStartDateTime) && !isNullOrUndefined(this.endDateTime)) {
            return dayjs(this.endDateTime).diff(dayjs(this.actualStartDateTime), 'minute');
        }
        else if (!isNullOrUndefined(this.startDateTime) && !isNullOrUndefined(this.actualEndDateTime)) {
            return dayjs(this.actualEndDateTime).diff(dayjs(this.startDateTime), 'minute');
        }
        else {
            return dayjs(this.endDateTime).diff(dayjs(this.startDateTime), 'minute');
        }
    }
    /**
     * gets all mots used by current connection compiled / summarized with calculated duration of each mot
     * @returns {DurationInformationMot[]}
     */
    get mots() {
        return this._partialConnections.map((partialConnection) => partialConnection.mot);
    }
    /**
     * gets all partial connections, iterates over them and checks if their MOT
     * is an actual traffic MOT, then increments the number of changes the connection has
     * @returns {number}
     */
    get changes() {
        let numberOfMots = 0;
        for (const partial of this._partialConnections) {
            if (partial.mot.category === TRANSPORTATION_CATEGORY.MOT) {
                numberOfMots++;
            }
            else if (partial.mot.category === TRANSPORTATION_CATEGORY.TRANSITION && partial.mot.type === TRANSITION_TYPE.STAY_IN_VEHICLE) {
                numberOfMots--;
            }
        }
        return numberOfMots === 0 ? numberOfMots : numberOfMots - 1;
    }
    /**
     * gets the arrays of the partial paths of all partial connections and concats them
     */
    get pathOnMap() {
        let pathOnMap = [];
        for (const partial of this._partialConnections) {
            pathOnMap = pathOnMap.concat(partial.pathOnMap);
        }
        return pathOnMap;
    }
    /**
     * Returns true if any of the partial connections is real time controlled
     */
    get realTimeControlled() {
        return !this.partialConnections.some((partial) => {
            return !partial.realTimeControlled;
        });
    }
}
//# sourceMappingURL=connection.js.map